import { STATUS_CODE } from '@kc-console/core';

import { printGuardLog } from '../../utils/printGuardLog';
import {
  createServerTraceGroupId,
  getServerTraceCategory,
  startServerTraceSpan,
} from '../../utils/serverTrace/store';

import type { BaseState, ServiceAppState } from '@kc-console/context';
import type { AnyOfGuard, Guard } from '../../types';
import type { ServerTraceOutcome } from '../../utils/serverTrace/types';

const getGuardOutcome = <T extends BaseState | ServiceAppState>(
  state: Partial<T>,
): ServerTraceOutcome => {
  if (state.redirect) return 'redirect';
  if (state.status && state.status !== STATUS_CODE.OK) return 'failed_status';
  return 'success';
};

export const combineGuardsSerial = <T extends BaseState | ServiceAppState>(
  guards: AnyOfGuard[],
): Guard<T> => {
  return async params => {
    const { state, context, queryClient } = params;
    const groupId = createServerTraceGroupId(context.req, 'serial');

    let newState = { ...state };

    for (const guard of guards) {
      if (newState.status && newState.status !== STATUS_CODE.OK) break;
      if (newState.redirect) break;

      const guardName = guard.displayName || guard.name;
      const span = startServerTraceSpan(context.req, {
        name: guardName,
        category: getServerTraceCategory(guard.traceCategory),
        execution: 'serial',
        groupId,
      });

      let fulfilledState: Partial<T>;

      try {
        fulfilledState = await (guard as unknown as Guard<T>)({
          context,
          state: newState as T,
          queryClient,
        });
      } catch (error) {
        span.end('rejected');
        throw error;
      }

      const mergedState = { ...newState, ...fulfilledState };
      span.end(getGuardOutcome(mergedState), mergedState.status);

      printGuardLog({
        context: params.context,
        state: { ...state, ...fulfilledState },
        guardName,
      });

      newState = mergedState;
    }

    return newState;
  };
};
